import { Toast } from "../Notifications/Toast.js";

const UNREAD_REPORT_INFO_ALERT_API = '/reports/get-user-ai-report-info';


export class WorklistSummaryBar extends HTMLElement
{

    TIER_COLORS = {
        'critical': '#aa0000',
        'high': '#aa4000',
        'moderate': '#aa8f00',
        'low': '#00aa00',
    };


    constructor()
    {
        super();
    }

    connectedCallback()
    {
        this.init();
    }

    init()
    {
        // initialize any necessary event handlers or state here
        this.checkForUnreadReports(true); // check immediately on page load, with unreadOnly=true to avoid showing toast for reports that have been added since last check but before page load
        this.initEventHandlers();
    }

    initEventHandlers()
    {
        document.body.addEventListener('toast-notification-click', this.handleUnreadReportToastClick.bind(this));
        document.body.addEventListener('five-minute-mark', this.handleFiveMinuteMark.bind(this));
    }

    handleFiveMinuteMark(event)
    {
        this.checkForUnreadReports(); // on every 5-minute mark, check for unread reports 
    }

    refreshUnreadReports(unreadReportsCount, unreadTierCounts = {})
    {
        // update the summary bar display with the new unread reports count
        const section = this.querySelector('section.unread-report-details');
        if (!section) {
            return;
        }
    
        // build from scratch
        section.innerHTML = '';
    
        if (unreadReportsCount == 0) {
            const svg = document.createElement('material-design-svg');
            svg.setAttribute('data-icon', 'filled__check_circle');
            svg.dataset.iconSize = '24';
            const hdrMsg = document.createElement('h5');
            hdrMsg.textContent = 'All Reports Reviewed';
            section.appendChild(svg);
            section.appendChild(hdrMsg);
            return;
        }
    
        // Flatten all tier buckets to build the main "X Unread Reports" link
        const allCaseIds = new Set();
        Object.values(unreadTierCounts).forEach(ids => {
            ids.forEach(id => allCaseIds.add(id));
        });
        const allCommaList = Array.from(allCaseIds).join(',');
    
        const worklistLink = document.createElement('a');
        worklistLink.classList.add('unread-reports-link');
        worklistLink.href = '/worklist/clinic?CaseID=' + allCommaList;
        worklistLink.textContent = `${unreadReportsCount} Unread Report${unreadReportsCount > 1 ? 's' : ''}`;
        section.appendChild(worklistLink);
    
        // Per-tier breakdown
        const tierList = document.createElement('ul');
        Object.keys(this.TIER_COLORS).forEach(tier => {
            const tierIds = unreadTierCounts[tier];
            if (!tierIds || tierIds.length === 0) {
                return;
            }
    
            const tierItem = document.createElement('li');
            const tierCount = tierIds.length;
            const commaList = tierIds.join(',');
    
            const tierLink = document.createElement('a');
            const capitalized = tier[0].toUpperCase() + tier.slice(1);
            tierLink.textContent = `${tierCount} ${capitalized}`;
            tierLink.style.color = this.TIER_COLORS[tier];
            tierLink.href = '/worklist/clinic?CaseID=' + commaList;
            const popOver = document.createElement('pop-over');
            popOver.textContent = 'Click to view';
            tierLink.appendChild(popOver);
    
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('viewBox', '0 0 20 20');
            svg.setAttribute('width', '10');
            svg.setAttribute('height', '10');
    
            const coloredCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            coloredCircle.classList.add('tier-indicator');
            coloredCircle.style.fill = this.TIER_COLORS[tier];
            coloredCircle.setAttribute('cx', '10');
            coloredCircle.setAttribute('cy', '10');
            coloredCircle.setAttribute('r', '10');
    
            svg.appendChild(coloredCircle);
            tierItem.appendChild(svg);
            tierItem.appendChild(tierLink);
            tierList.appendChild(tierItem);
        });
        section.appendChild(tierList);
    }  
    
    checkForUnreadReports(unreadOnly = false)
    {
        // if user is on worklist page, need to send extra query param 
        const onWorklistPage = window.location.pathname.includes('/worklist/clinic');
        const apiUrl = onWorklistPage ? `${UNREAD_REPORT_INFO_ALERT_API}?iurd=1` : UNREAD_REPORT_INFO_ALERT_API;
        fetch(apiUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json().then(data => {
                    if (data.success) {
                        const recently_added_reports = data.recently_added_reports || 0;
                        if ( !unreadOnly && recently_added_reports > 0) {
                            this.showToastNotification(data);
                        }
                        const unread_reports = data.unread_reports || 0;
                        const unread_tier_counts = data.unread_tier_counts || {};
                        if (onWorklistPage) {
                            this.refreshUnreadReports(unread_reports, unread_tier_counts);
                        }
                    }
                });
            })
            .catch(error => {
                console.error('Error fetching unread report count:', error);
            });
    }

    showToastNotification(data) {
        const recently_added_reports = data.recently_added_reports || 0;
        let message = "";
        if (recently_added_reports === 1) {
            message = this.getSingularReportMessage(data);
        } else {
            message = this.getMultipleReportsMessage(data);
        }

        const caseIds = new Set();
        data.recently_added_report_data.forEach(report => {
            caseIds.add(report.case_id);
        });
        const commaList = Array.from(caseIds).join(',');
        const datasetList = [
            { key: 'case_ids', value: commaList }
        ];

        const toast = new Toast({
            bodyContent: message,
            headerContent: "Unread Report Alert",
            delay: 10000, // 10 seconds
            containerClassList: ['unread-report-toast-container'],
            datasetList: datasetList,
            hideOthers: true
        });
        toast.show();
    }

    getSingularReportMessage(data) {
        const report = data.recently_added_report_data[0];
        let message = "One new report ready - ";
        if (report.findings.length == 1) {
            message += `${ report.findings[0] }`;        
        } else {
            message += `${ report.findings[0] } and ${ report.findings.length - 1 } others`;
        }
        message += `<br/> Click on this message to review the case.`;
        return message;
    }

    getMultipleReportsMessage(data) {
        const recently_added_reports = data.recently_added_reports || 0;
        // Tally reports by tier label, ordered by urgency
        const tierOrder = ['Critical', 'High', 'Moderate', 'Low'];
        const tierCounts = {};
        data.recently_added_report_data.forEach(report => {
            tierCounts[report.label] = (tierCounts[report.label] || 0) + 1;
        });
  
        const tierParts = tierOrder
            .filter(label => tierCounts[label])
            .map(label => `${tierCounts[label]} ${label}`);
  
        let message = `${recently_added_reports} new reports ready`;
        if (tierParts.length) {
            message += ` - ${tierParts.join(', ')}`;
        }
        message += `<br/> Click on this message to review the cases.`;
        return message;
  
    }

    handleUnreadReportToastClick(event)
    {
        const toastElem = event.detail.originalEvent.currentTarget;
        const toastContainerElem = toastElem.closest('.unread-report-toast-container');
        if (toastElem && toastContainerElem) {
            const caseIds = toastElem.dataset.case_ids;
            window.location.href = '/worklist/clinic?CaseID=' + caseIds;
        }
    }


}

customElements.define("worklist-summary-bar", WorklistSummaryBar);